interface AuthPopupOptions {
  matchUrl: (href: string) => boolean;
  onMatch: (href: string) => void;
  windowName?: string;
  width?: number;
  height?: number;
  pollIntervalMs?: number;
  timeoutMs?: number;
  fallbackUrl?: string;
  onBlocked?: () => void;
  onTimeout?: () => void;
}

const defaultWidth = 600;
const defaultHeight = 700;
const defaultPollInterval = 500;
const defaultTimeout = 2 * 60 * 1000;

const createPopup = (
  url: string,
  name: string,
  width: number,
  height: number
) => {
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  return window.open(
    url,
    name,
    `width=${width},height=${height},left=${left},top=${top}`
  );
};

export const openAuthPopup = (authUrl: string, options: AuthPopupOptions) => {
  const {
    matchUrl,
    onMatch,
    windowName = "auth-popup",
    width = defaultWidth,
    height = defaultHeight,
    pollIntervalMs = defaultPollInterval,
    timeoutMs = defaultTimeout,
    fallbackUrl,
    onBlocked,
    onTimeout,
  } = options;

  const fallback = () => {
    if (onTimeout) {
      onTimeout();
    }

    window.location.href = fallbackUrl ?? authUrl;
  };

  const popup = createPopup(authUrl, windowName, width, height);

  if (!popup) {
    if (onBlocked) {
      onBlocked();
    } else {
      window.location.href = authUrl;
    }
    return;
  }

  popup.focus();

  const startTime = Date.now();
  const timer = window.setInterval(() => {
    let isClosed = false;

    try {
      isClosed = popup.closed;
    } catch (error) {
      // Access to popup.closed may be blocked by COOP while on a foreign origin.
      // Ignore the error and continue polling until we regain access.
      return;
    }

    if (isClosed) {
      window.clearInterval(timer);
      return;
    }

    if (Date.now() - startTime > timeoutMs) {
      window.clearInterval(timer);
      popup.close();
      fallback();
      return;
    }

    try {
      const popupHref = popup.location.href;

      if (!popupHref) {
        return;
      }

      if (!matchUrl(popupHref)) {
        return;
      }

      window.clearInterval(timer);
      onMatch(popupHref);
      popup.close();
    } catch (error) {
      // Ignore cross-origin access errors while the popup is on the provider domain.
      return;
    }
  }, pollIntervalMs);
};
